import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Button,
  Container,
  Flex,
  HStack,
  Icon,
  SimpleGrid,
  Text,
  Tooltip,
  VStack,
  useColorMode,
} from "@chakra-ui/react";
import {
  FiChevronDown,
  FiChevronUp,
  FiCheck,
  FiEdit2,
  FiEye,
  FiEyeOff,
  FiGrid,
  FiRotateCcw,
} from "react-icons/fi";
import { useMutation } from "@apollo/client";
import { useAuth } from "../contexts/AuthContext";
import { useModulePermissions } from "../contexts/ModulePermissionsContext";
import { allModuleConfigs, moduleIcons } from "./FloatingNavbar";
import { SAVE_MY_QUICKLINKS, QUICKLINKS_MIRROR_KEY } from "./quickLinksQueries";
import { ModuleConfig } from "../types/ModuleConfig";

/**
 * AdminModuleLinks — the home-page quick links.
 *
 * ONE GROUP VARIANT (2026-09-16, Tom): the tiles are the PAGES of a module — CEO Dashboard,
 * Commission Board, Money, Activity, … — not the list of modules. They come from each listed
 * module's own `navigation` entries in its moduleConfig, so a page added to a module's nav
 * appears here without a second list to maintain. 2026-10-20 (Tom): Track My Build
 * (`build-portal`) gets its own section under the Command Centre's (`QUICK_LINK_SECTIONS`), and the Comms Log
 * (`comms-log`) one under that — each shown only to the people its own permissions let in.
 *
 * Shown at the top of the home page to any logged-in user who may open at least one of those
 * pages; a section with no openable page is left out. Anonymous visitors, and users with no
 * access at all, get nothing (null).
 *
 * Gating mirrors FloatingNavbar.getAvailableModules: the backend-resolved permissions
 * (ModulePermissionsContext, the SSOT) for each module decide, per entry, whether the user
 * holds its `permissionLevel` (`view` = view ∪ self; any other level = that level's list).
 * The grid therefore always agrees with the nav drawer.
 *
 * Order (rule 45, 2026-10-20): each section is A–Z by label until the person curates it.
 * Their saved order wins once set; a page shipped later lands at the end of its section, A–Z.
 * Edit mode says which order each section is in.
 *
 * Curation is per person (mobile-first — no drag): Edit mode adds Up / Down /
 * Hide / Show buttons to every tile (Up / Down move within the tile's section), and
 * Reset returns every section to A–Z with nothing hidden.
 * Persistence is server-side (`saveMyQuickLinks` → Client.preferences.quickLinks,
 * read back through useAuth) with a localStorage mirror so the layout shows
 * instantly on load and survives an auth refetch. The server value wins when it
 * arrives. The saved lists hold paths, which are unique across sections.
 *
 * Nothing here claims success before the server confirms: the status line says
 * "Saving…", then "Saved to your login" only after the mutation resolves, and
 * names the failure otherwise.
 */

interface QuickLinkPrefs {
  order: string[]; // module ids, in the user's chosen order
  hidden: string[]; // module ids hidden from the (non-edit) grid
}

const EMPTY_PREFS: QuickLinkPrefs = { order: [], hidden: [] };

const asStringArray = (v: unknown): string[] =>
  Array.isArray(v) ? v.filter((x): x is string => typeof x === "string") : [];

/**
 * Pages whose path changed: a saved order/hidden list still holds the OLD path, so it is read as
 * the new one — the tile keeps its place (26 Oct 2026: the CEO Dashboard became `/command/dashboard`).
 */
const RENAMED_PATHS: Record<string, string> = {
  "/command/ceo?unit=property": "/command/dashboard",
};

const asPathArray = (v: unknown): string[] =>
  Array.from(new Set(asStringArray(v).map((p) => RENAMED_PATHS[p] || p)));

const samePrefs = (a: QuickLinkPrefs, b: QuickLinkPrefs): boolean =>
  a.order.join(" ") === b.order.join(" ") && a.hidden.join(" ") === b.hidden.join(" ");

export const readQuickLinksMirror = (): QuickLinkPrefs | null => {
  try {
    const raw = localStorage.getItem(QUICKLINKS_MIRROR_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object") return null;
    return { order: asPathArray(parsed.order), hidden: asPathArray(parsed.hidden) };
  } catch {
    return null;
  }
};

export const writeQuickLinksMirror = (prefs: QuickLinkPrefs): void => {
  try {
    localStorage.setItem(QUICKLINKS_MIRROR_KEY, JSON.stringify(prefs));
  } catch {
    /* storage unavailable (private mode) — the server copy still exists */
  }
};

/** The modules whose pages become tiles, one section each, in this order on the page. */
interface QuickLinkSection {
  moduleId: string;
  title: string;
  blurb: string;
  /**
   * A nav level the backend manifest does not send yet, and the module + level whose list stands in for it.
   * The real list wins as soon as the manifest carries it.
   */
  levelStandIns?: Record<string, { moduleId: string; level: string }>;
}

const QUICK_LINK_SECTIONS: QuickLinkSection[] = [
  { moduleId: "command", title: "Command Centre — quick links", blurb: "Every page of the Command Centre, one tap away." },
  // 27 Oct 2026: One Finance's loan book — shown only to people holding a FINANCE_* permission.
  { moduleId: "finance", title: "One Finance — quick links", blurb: "The loan book, its forecast and the monthly statements." },
  {
    moduleId: "build-portal",
    title: "Track My Build — quick links",
    blurb: "Every page of Track My Build, one tap away.",
    // `staff` is not a field of the modulePermissions query (2026-10-20). The backend manifest defines it as the
    // Command Centre's view list (build-portal/permissions.ts), so a build client never gets the team's pages.
    levelStandIns: { staff: { moduleId: "command", level: "view" } },
  },
  // 20 Oct 2026, Tom: "put buttons to the comms-log module on the quick links ... so it shows up for the people that have access".
  { moduleId: "comms-log", title: "Comms Log — quick links", blurb: "Every text and email the site has sent, and its settings." },
];

/**
 * Pages kept in the nav but left off the home quick links — the team does not use them day to day
 * (Tom, 17 Sep 2026). The saved order/hidden lists are unaffected: these simply never become tiles.
 */
const NOT_QUICK_LINKS = new Set(["/command/sources", "/command/import", "/command/leads", "/command/deals"]);

/** One tile: a page of a module. `id` is the path — it is what the saved order/hidden lists hold. */
interface Tile {
  id: string;
  name: string;
  path: string;
  icon?: string;
  accent: string;
}

/** A section as shown: its tiles in the order on screen, and whether that order is the person's own. */
interface SectionView {
  section: QuickLinkSection;
  tiles: Tile[];
  curated: boolean;
}

const byLabel = (a: Tile, b: Tile) => a.name.localeCompare(b.name, undefined, { sensitivity: "base", numeric: true });

// Nav entries carry an emoji (e.g. "📊"); a module config may carry a react-icons name
// (e.g. "FiGrid"). Render the real glyph for the latter via the shared moduleIcons map.
const TileGlyph = ({ tile, moduleId }: { tile: Tile; moduleId: string }) => {
  const isIconName = /^Fi[A-Z]/.test(tile.icon || "");
  if (isIconName || !tile.icon) {
    const Comp = moduleIcons[moduleId] || FiGrid;
    return <Icon as={Comp} boxSize={6} aria-hidden />;
  }
  return (
    <Text fontSize="2xl" lineHeight="1" aria-hidden>
      {tile.icon}
    </Text>
  );
};

type SaveState = "idle" | "saving" | "saved" | "error";

const AdminModuleLinks = () => {
  const { isAuthenticated, user } = useAuth();
  const { permissions: backendPerms } = useModulePermissions();
  const { colorMode } = useColorMode();

  const [editing, setEditing] = useState(false);
  // Mirror first: the layout appears instantly, before myClient resolves.
  const [prefs, setPrefs] = useState<QuickLinkPrefs>(() => readQuickLinksMirror() ?? EMPTY_PREFS);
  const [saveState, setSaveState] = useState<SaveState>("idle");
  const [saveError, setSaveError] = useState<string | null>(null);

  const [saveMutation] = useMutation(SAVE_MY_QUICKLINKS);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pendingRef = useRef<QuickLinkPrefs | null>(null);

  // Server value (Client.preferences.quickLinks via useAuth). Only `myClient` selects
  // `preferences`; the email/phone fallback queries don't. So: no user, or a user object
  // WITHOUT a `preferences` key = the server hasn't spoken yet (keep the mirror);
  // `preferences` present but `quickLinks` null = the user never saved a layout (empty wins).
  const serverPreferences = user?.preferences;
  const serverPrefs = useMemo<QuickLinkPrefs | null>(() => {
    if (!user || serverPreferences === undefined) return null;
    const ql = serverPreferences?.quickLinks;
    return { order: asPathArray(ql?.order), hidden: asPathArray(ql?.hidden) };
  }, [user, serverPreferences]);

  // Server wins when it arrives (and on every auth refetch) — unless a local edit is
  // still waiting to be written, in which case the local edit is the newer truth.
  useEffect(() => {
    if (!serverPrefs) return;
    if (pendingRef.current) return;
    setPrefs((current) => (samePrefs(current, serverPrefs) ? current : serverPrefs));
    writeQuickLinksMirror(serverPrefs);
  }, [serverPrefs]);

  useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, []);

  // Local update + mirror now; debounced write to the server (batches rapid taps).
  const savePrefs = useCallback(
    (next: QuickLinkPrefs) => {
      setPrefs(next);
      writeQuickLinksMirror(next);
      pendingRef.current = next;
      setSaveState("saving");
      setSaveError(null);
      if (debounceRef.current) clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(() => {
        const toSave = pendingRef.current;
        if (!toSave) return;
        saveMutation({ variables: { order: toSave.order, hidden: toSave.hidden } })
          .then(() => {
            if (pendingRef.current === toSave) {
              pendingRef.current = null;
              setSaveState("saved");
            }
          })
          .catch((err: unknown) => {
            if (pendingRef.current === toSave) pendingRef.current = null;
            setSaveState("error");
            setSaveError(err instanceof Error ? err.message : "Unknown error");
          });
      }, 400);
    },
    [saveMutation]
  );

  // Each section's pages THIS USER may open, A–Z. Each nav entry names the level it needs;
  // the backend-resolved manifest says who holds it.
  const userPerms: string[] | undefined = user?.permissions;
  const baseSections = useMemo<{ section: QuickLinkSection; tiles: Tile[] }[]>(() => {
    if (!userPerms || userPerms.length === 0) return [];
    return QUICK_LINK_SECTIONS.map((section) => {
      const mod: ModuleConfig | undefined = allModuleConfigs.find((m) => m.id === section.moduleId && m.enabled !== false);
      if (!mod) return { section, tiles: [] };
      const all = backendPerms as Record<string, any> | undefined;
      const perms = all?.[mod.id] ?? mod.permissions;
      const holds = (level: string | undefined): boolean => {
        // Any level other than view reads its own list — `admin` (Integrations, owners only) must
        // never fall through to view, or the tile shows to everyone who can read the board.
        let gate: string[] =
          !level || level === "view"
            ? [...asStringArray(perms?.view), ...asStringArray(perms?.self)]
            : asStringArray(perms?.[level]);
        const standIn = level ? section.levelStandIns?.[level] : undefined;
        if (gate.length === 0 && standIn) gate = asStringArray(all?.[standIn.moduleId]?.[standIn.level]);
        return userPerms.some((p) => gate.includes(p));
      };
      const seen = new Set<string>();
      const tiles = (mod.navigation || [])
        .filter((n) => n.path && !n.path.includes(":") && !NOT_QUICK_LINKS.has(n.path) && holds(n.permissionLevel))
        .filter((n) => (seen.has(n.path) ? false : (seen.add(n.path), true)))
        .map((n) => ({ id: n.path, name: n.label, path: n.path, icon: n.icon, accent: mod.color || "#5AC8FA" }))
        .sort(byLabel);
      return { section, tiles };
    }).filter((s) => s.tiles.length > 0);
  }, [backendPerms, userPerms]);

  // Apply the saved order within each section; any page not yet in the saved order (e.g. a
  // newly shipped page) falls in at the end of its section, A–Z, so nothing vanishes.
  const sections = useMemo<SectionView[]>(
    () =>
      baseSections.map(({ section, tiles }) => {
        const byId = new Map(tiles.map((t) => [t.id, t]));
        const ordered: Tile[] = [];
        prefs.order.forEach((id) => {
          const item = byId.get(id);
          if (item) {
            ordered.push(item);
            byId.delete(id);
          }
        });
        const curated = ordered.length > 0;
        tiles.forEach((t) => {
          if (byId.has(t.id)) ordered.push(t);
        });
        return { section, tiles: ordered, curated };
      }),
    [baseSections, prefs.order]
  );

  const hiddenSet = useMemo(() => new Set(prefs.hidden), [prefs.hidden]);

  // Up / Down swap within the tile's own section. The saved order keeps every other
  // section's entries as they were and appends this section's new order.
  const move = useCallback(
    (sectionTiles: Tile[], id: string, dir: -1 | 1) => {
      const ids = sectionTiles.map((x) => x.id);
      const i = ids.indexOf(id);
      const j = i + dir;
      if (i < 0 || j < 0 || j >= ids.length) return;
      [ids[i], ids[j]] = [ids[j], ids[i]];
      const mine = new Set(ids);
      savePrefs({ ...prefs, order: [...prefs.order.filter((x) => !mine.has(x)), ...ids] });
    },
    [prefs, savePrefs]
  );

  const toggleHidden = useCallback(
    (id: string) => {
      const next = new Set(prefs.hidden);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      savePrefs({ ...prefs, hidden: Array.from(next) });
    },
    [prefs, savePrefs]
  );

  const reset = useCallback(() => savePrefs(EMPTY_PREFS), [savePrefs]);

  // --- gate AFTER all hooks (rules-of-hooks) ---
  if (!isAuthenticated || sections.length === 0) return null;

  const cardBg = colorMode === "light" ? "#FFFFFF" : "rgba(30,30,30,0.7)";
  const cardBorder = colorMode === "light" ? "rgba(20, 28, 73, 0.12)" : "rgba(255,255,255,0.12)";
  const textColor = colorMode === "light" ? "#141C49" : "gray.100";
  const labelColor = colorMode === "light" ? "gray.600" : "gray.400";

  const statusLine =
    saveState === "saving"
      ? "Saving to your login…"
      : saveState === "saved"
      ? "Saved to your login."
      : saveState === "error"
      ? `Couldn't save to your login (${saveError ?? "unknown error"}). Your layout is kept on this device for now — try again after your next change.`
      : null;

  return (
    <Container maxW="7xl" pt={6} pb={2} px={{ base: 4, md: 6 }} position="relative" zIndex={2}>
      <VStack align="stretch" spacing={6}>
        {sections.map(({ section, tiles, curated }) => {
          const items = editing ? tiles : tiles.filter((x) => !hiddenSet.has(x.id));
          const hiddenCount = tiles.filter((x) => hiddenSet.has(x.id)).length;
          return (
            <Box key={section.moduleId} as="section" aria-label={section.title}>
              <Text as="h2" fontSize="lg" fontWeight="bold" color={textColor} mb={1}>
                {section.title}
              </Text>
              <Text fontSize="sm" color={labelColor} mb={3}>
                {editing
                  ? curated
                    ? "Order: yours. Up and Down move a page within this section; Reset puts it back A–Z."
                    : "Order: A–Z. Move a page with Up or Down and this section keeps your order."
                  : `${section.blurb} Edit to reorder or hide. Saved to your login. Each link opens in a new tab.`}
              </Text>

              {items.length === 0 && !editing ? (
                <Text fontSize="md" color={labelColor}>
                  All {hiddenCount} of these pages are hidden — press <b>Edit</b> below to show some again.
                </Text>
              ) : (
                <SimpleGrid columns={editing ? { base: 1, sm: 2, md: 3, lg: 4 } : { base: 2, sm: 3, md: 4, lg: 6 }} spacing={3}>
                  {items.map((tile, index) => {
                    const { id, name, path, accent } = tile;
                    const isHidden = hiddenSet.has(id);

                    const inner = (
                      <VStack spacing={1} align="center" opacity={editing && isHidden ? 0.45 : 1}>
                        <TileGlyph tile={tile} moduleId={section.moduleId} />
                        <Text
                          color={textColor}
                          fontWeight="semibold"
                          fontSize="sm"
                          textAlign="center"
                          noOfLines={2}
                          title={name}
                        >
                          {name}
                        </Text>
                        {editing && isHidden && (
                          <Text fontSize="sm" color={labelColor}>
                            Hidden
                          </Text>
                        )}
                      </VStack>
                    );

                    const cardProps = {
                      bg: cardBg,
                      border: "1px solid",
                      borderColor: cardBorder,
                      borderLeft: "3px solid",
                      borderLeftColor: accent,
                      borderRadius: "xl",
                      p: 3,
                      minW: 0,
                      minH: "88px",
                    } as const;

                    if (!editing) {
                      return (
                        <Box
                          key={id}
                          as="a"
                          href={path}
                          target="_blank"
                          rel="noopener noreferrer"
                          aria-label={`Open ${name} in a new tab`}
                          textDecoration="none"
                          display="flex"
                          alignItems="center"
                          justifyContent="center"
                          transition="all 0.2s"
                          _hover={{ transform: "translateY(-2px)", boxShadow: "md", borderColor: accent }}
                          {...cardProps}
                        >
                          {inner}
                        </Box>
                      );
                    }

                    // Edit mode: a tile with big, worded tap targets; no navigation.
                    return (
                      <Box key={id} {...cardProps}>
                        {inner}
                        <HStack spacing={2} justify="center" mt={3} flexWrap="wrap" rowGap={2}>
                          <Tooltip hasArrow openDelay={300} label="Move this tile one place earlier in its section. Saves to your login.">
                            <Button
                              size="sm"
                              minH="44px"
                              variant="outline"
                              leftIcon={<FiChevronUp />}
                              isDisabled={index === 0}
                              onClick={() => move(tiles, id, -1)}
                            >
                              Up
                            </Button>
                          </Tooltip>
                          <Tooltip hasArrow openDelay={300} label="Move this tile one place later in its section. Saves to your login.">
                            <Button
                              size="sm"
                              minH="44px"
                              variant="outline"
                              rightIcon={<FiChevronDown />}
                              isDisabled={index === items.length - 1}
                              onClick={() => move(tiles, id, 1)}
                            >
                              Down
                            </Button>
                          </Tooltip>
                          <Tooltip
                            hasArrow
                            openDelay={300}
                            label={
                              isHidden
                                ? "Show this tile in your quick links again. Saves to your login."
                                : "Hide this tile from your quick links (you can show it again here). Saves to your login."
                            }
                          >
                            <Button
                              size="sm"
                              minH="44px"
                              variant={isHidden ? "solid" : "outline"}
                              colorScheme="blue"
                              leftIcon={isHidden ? <FiEye /> : <FiEyeOff />}
                              onClick={() => toggleHidden(id)}
                            >
                              {isHidden ? "Show" : "Hide"}
                            </Button>
                          </Tooltip>
                        </HStack>
                      </Box>
                    );
                  })}
                </SimpleGrid>
              )}
            </Box>
          );
        })}
      </VStack>

      {/* Controls at the BOTTOM — under the thumb on a phone. One Edit for every section. */}
      <Flex justify="center" mt={4} gap={2} flexWrap="wrap">
        {editing && (
          <Tooltip
            hasArrow
            openDelay={300}
            label="Put every section back in A–Z order and show every tile. This writes to your login."
          >
            <Button size="md" minH="44px" variant="ghost" colorScheme="red" leftIcon={<FiRotateCcw />} onClick={reset}>
              Reset
            </Button>
          </Tooltip>
        )}
        <Tooltip
          hasArrow
          openDelay={300}
          label={
            editing
              ? "Leave edit mode. Your changes are already being saved to your login as you make them."
              : "Reorder or hide your quick links. Every change is saved to your login as you make it."
          }
        >
          <Button
            size="md"
            minH="44px"
            variant={editing ? "solid" : "outline"}
            colorScheme={editing ? "green" : "gray"}
            leftIcon={editing ? <FiCheck /> : <FiEdit2 />}
            onClick={() => setEditing((e) => !e)}
          >
            {editing ? "Done" : "Edit"}
          </Button>
        </Tooltip>
      </Flex>

      {statusLine && (
        <Text
          role="status"
          aria-live="polite"
          fontSize="sm"
          textAlign="center"
          mt={2}
          color={saveState === "error" ? "red.500" : labelColor}
        >
          {statusLine}
        </Text>
      )}
    </Container>
  );
};

export default AdminModuleLinks;
